import React, { useState } from 'react';
import { mergeGuestAccountAndJoinClubs } from '../../services/guestAccountLinkingService';
import Button from '../common/Button';
import { Modal, ModalContent } from '../common';
import { Card } from '../common/Card';
import { Heading, Text, Typography } from '../common/Typography';

/**
 * GuestAccountBulkMergeModal Component
 * Handles merging multiple guest accounts at once
 */
const GuestAccountBulkMergeModal = ({
  guestIds,
  userId,
  guestAccounts,
  onClose,
  onMergeComplete
}) => {
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState(null);
  const [mergeResult, setMergeResult] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  const handleBulkMerge = async () => {
    try {
      setMerging(true);
      setError(null);
      setCurrentStep(0);

      const individualResults = [];
      let totalGamesMoved = 0;
      let totalClubsJoined = 0;

      for (let i = 0; i < guestAccounts.length; i += 1) {
        setCurrentStep(i + 1);
        const guest = guestAccounts[i];
        const clubIds = (guest.players_club || [])
          .filter((pc) => pc.is_active !== false)
          .map((pc) => pc.club_id)
          .filter(Boolean);

        try {
          const result = await mergeGuestAccountAndJoinClubs(guest.id, userId, clubIds);
          individualResults.push({
            guest_id: guest.id,
            status: 'merged',
            ...result,
            merge_club_ids: clubIds.length ? clubIds : undefined,
          });
          totalGamesMoved += result.games_moved || 0;
          totalClubsJoined +=
            result.clubs_joined?.length ?? result.clubs_affected ?? clubIds.length;
        } catch (err) {
          individualResults.push({
            guest_id: guest.id,
            status: 'failed',
            error: err.message || 'Merge failed',
          });
        }
      }

      const successCount = individualResults.filter((r) => r.status === 'merged').length;
      const failedCount = individualResults.length - successCount;

      if (successCount === 0) {
        throw new Error('No guest accounts could be merged');
      }

      const result = {
        status: 'completed',
        success_count: successCount,
        failed_count: failedCount,
        total_games_moved: totalGamesMoved,
        total_clubs_affected: totalClubsJoined,
        individual_results: individualResults,
      };

      setMergeResult(result);
      if (onMergeComplete) {
        onMergeComplete(result);
      }
    } catch (err) {
      console.error('Error bulk merging guest accounts:', err);
      setError(err.message || 'Failed to merge guest accounts');
    } finally {
      setMerging(false);
    }
  };

  const successCount = mergeResult?.success_count || 0;
  const failedCount = mergeResult?.failed_count || 0;

  return (
    <Modal isOpen onClose={onClose} closeOnBackdropClick={!merging}>
      <ModalContent size="large" className="flex min-h-0 flex-col overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto">
          {mergeResult ? (
            <>
              <Heading level={3} className="mb-4">Bulk Merge Complete</Heading>

              <div className="space-y-4 mb-6">
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <Text className="text-green-800">
                    <strong>Summary:</strong>
                  </Text>
                  <ul className="list-disc list-inside mt-2 text-sm text-green-800 space-y-1">
                    <li>{successCount} account{successCount !== 1 ? 's' : ''} merged successfully</li>
                    {failedCount > 0 && (
                      <li className="text-yellow-800">{failedCount} account{failedCount !== 1 ? 's' : ''} failed to merge</li>
                    )}
                    <li>{mergeResult.total_games_moved || 0} total games transferred</li>
                    <li>{mergeResult.total_clubs_affected || 0} club memberships joined</li>
                  </ul>
                </div>

                {mergeResult.individual_results && mergeResult.individual_results.length > 0 && (
                  <div>
                    <Typography variant="strong" className="mb-2">Individual Results:</Typography>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {mergeResult.individual_results.map((result, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded-lg ${
                            result.status === 'merged'
                              ? 'bg-green-50 border border-green-200'
                              : 'bg-red-50 border border-red-200'
                          }`}
                        >
                          <Text className="text-sm">
                            {result.status === 'merged' ? '✓' : '✗'} {result.guest_id || 'Unknown'} - {result.status}
                            {result.error && `: ${result.error}`}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button onClick={onClose}>Done</Button>
              </div>
            </>
          ) : (
            <>
              <Heading level={3} className="mb-4">Merge All Guest Accounts</Heading>

              {error && (
                <Card variant="danger" size="small" className="mb-4">
                  <Text className="text-red-600">{error}</Text>
                </Card>
              )}

              <div className="space-y-4 mb-6">
                <Text>
                  You are about to merge <strong>{guestIds.length}</strong> guest account{guestIds.length !== 1 ? 's' : ''} into your account.
                </Text>

                <div>
                  <Typography variant="strong" className="mb-2">Accounts to merge:</Typography>
                  <div className="bg-slate-50 p-4 rounded-lg space-y-2 max-h-64 overflow-y-auto">
                    {guestAccounts.map((guest) => (
                      <div key={guest.id} className="text-sm">
                        <Text>• {guest.first_name} {guest.last_name || ''}</Text>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <Text className="text-sm text-blue-800">
                    <strong>Note:</strong> All game history, ELO ratings, and partnerships will transfer to your
                    account and you will be joined to each guest&apos;s club(s) as a member. This cannot be undone.
                  </Text>
                </div>

                {merging && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <Text className="text-sm text-slate-500">
                      Merging accounts... ({currentStep}/{guestAccounts.length})
                    </Text>
                  </div>
                )}
              </div>

              <div className="flex gap-3 justify-end">
                <Button variant="outline" onClick={onClose} disabled={merging}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleBulkMerge}
                  disabled={merging}
                >
                  {merging ? 'Merging...' : 'Merge All Accounts'}
                </Button>
              </div>
            </>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default GuestAccountBulkMergeModal;
